import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { Plus, Upload, AlertTriangle, Trash2, Pencil, X, Save, RotateCcw } from "lucide-react";
import { PushToProdButton } from "@/components/PushToProdButton";

const empty = {
  product_sku: "", batch_number: "", purity_percent: "", testing_lab: "",
  test_date: "", testing_methods: "HPLC,MS", retest_date: "", status: "active",
};

const STATUS_OPTIONS = ["active", "expired", "recalled", "under_review"];

export default function COAs() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // COA row currently being edited

  const load = () => api.get("/admin/coas").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/coas", { ...form, testing_methods: form.testing_methods.split(",").map((s) => s.trim()) });
      toast.success("COA created");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const upload = async (coa, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/admin/coas/${coa.id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF uploaded");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
  };

  const recall = async (coa) => {
    if (!confirm(`Recall batch ${coa.batch_number}? It will still appear in the public COA Hub but flagged as recalled.`)) return;
    try {
      await api.post(`/admin/coas/${coa.id}/recall`);
      toast.success(`Batch ${coa.batch_number} recalled`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Recall failed");
    }
  };

  const remove = async (coa) => {
    if (!confirm(`Delete COA for batch ${coa.batch_number}? This permanently removes the record. Use "Recall" instead if you want to keep it visible with a recall flag.`)) return;
    try {
      await api.delete(`/admin/coas/${coa.id}`);
      toast.success(`COA ${coa.batch_number} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const openEdit = (coa) => setEditing({
    ...coa,
    testing_methods: Array.isArray(coa.testing_methods) ? coa.testing_methods.join(", ") : (coa.testing_methods || ""),
  });

  const saveEdit = async () => {
    setBusy(true);
    try {
      const body = {
        batch_number: editing.batch_number,
        product_sku: editing.product_sku,
        purity_percent: editing.purity_percent,
        testing_lab: editing.testing_lab,
        test_date: editing.test_date,
        retest_date: editing.retest_date,
        testing_methods: editing.testing_methods, // backend splits string form
        status: editing.status,
      };
      await api.patch(`/admin/coas/${editing.id}`, body);
      toast.success(`Batch ${editing.batch_number} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="label-eyebrow">Quality</div>
      <h1 className="text-2xl font-bold tracking-tight mt-1">COAs & Batch Verification</h1>

      <div className="mt-8 border border-[#D1D5DB] p-5" data-testid="coa-create-card">
        <div className="label-eyebrow mb-3"><Plus className="inline h-3.5 w-3.5 mr-1" /> New COA</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <F label="Product SKU" v={form.product_sku} on={(v) => setForm({ ...form, product_sku: v })} test="coa-sku" />
          <F label="Batch number" v={form.batch_number} on={(v) => setForm({ ...form, batch_number: v })} test="coa-batch" />
          <F label="Purity %" v={form.purity_percent} on={(v) => setForm({ ...form, purity_percent: v })} test="coa-purity" />
          <F label="Testing lab" v={form.testing_lab} on={(v) => setForm({ ...form, testing_lab: v })} test="coa-lab" />
          <F label="Test date (YYYY-MM-DD)" v={form.test_date} on={(v) => setForm({ ...form, test_date: v })} test="coa-date" />
          <F label="Retest date (YYYY-MM-DD)" v={form.retest_date} on={(v) => setForm({ ...form, retest_date: v })} test="coa-retest" />
          <F label="Methods (comma-sep)" v={form.testing_methods} on={(v) => setForm({ ...form, testing_methods: v })} test="coa-methods" />
        </div>
        <button onClick={create} disabled={busy} className="btn-primary mt-4 inline-flex items-center gap-2 disabled:bg-[#5E636E]" data-testid="coa-create">
          {busy ? "Creating…" : "Create COA record"}
        </button>
      </div>

      <div className="mt-8 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Batch</th>
              <th className="text-left px-4 py-2">SKU</th>
              <th className="text-left px-4 py-2">Purity</th>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Lab</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`admin-coa-row-${c.batch_number}`}>
                <td className="px-4 py-3 font-mono">{c.batch_number}</td>
                <td className="px-4 py-3 font-mono">{c.product_sku}</td>
                <td className="px-4 py-3 font-mono">{c.purity_percent}%</td>
                <td className="px-4 py-3 font-mono text-xs">{c.test_date}</td>
                <td className="px-4 py-3 text-xs">{c.testing_lab}</td>
                <td className="px-4 py-3"><ComplianceBadge kind="coa" value={c.status} /></td>
                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                  <button
                    onClick={() => openEdit(c)}
                    className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline inline-flex items-center gap-1"
                    data-testid={`edit-coa-${c.batch_number}`}
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <label className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline cursor-pointer">
                    <Upload className="h-3.5 w-3.5" /> {c.coa_pdf_url ? "Replace PDF" : "Upload PDF"}
                    <input type="file" accept=".pdf" className="hidden" onChange={(e) => upload(c, e.target.files[0])} />
                  </label>
                  {c.status !== "recalled" && (
                    <button onClick={() => recall(c)} className="text-xs font-mono uppercase tracking-wider text-[#A30414] hover:underline inline-flex items-center gap-1" data-testid={`recall-${c.batch_number}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Recall
                    </button>
                  )}
                  <PushToProdButton
                    endpoint={`/admin/coas/${c.id}/push-to-production`}
                    label={c.batch_number}
                    entity="COA"
                    testid={`coa-push-${c.batch_number}`}
                    showOverride={false}
                  />
                  <button onClick={() => remove(c)} className="text-xs font-mono uppercase tracking-wider text-[#A30414] hover:underline inline-flex items-center gap-1" data-testid={`delete-coa-${c.batch_number}`}>
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditModal
          coa={editing}
          setCoa={setEditing}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
          busy={busy}
        />
      )}
    </div>
  );
}

function EditModal({ coa, setCoa, onCancel, onSave, busy }) {
  const set = (k, v) => setCoa({ ...coa, [k]: v });
  const dirty = useMemo(() => JSON.stringify(coa), [coa]); // just to keep the button responsive; not used strictly
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="edit-coa-modal">
      <div className="bg-white w-full max-w-2xl border border-[#D1D5DB] rounded-md shadow-xl mt-8 md:mt-0">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="label-eyebrow">Edit COA</div>
            <div className="font-semibold mt-0.5">Batch <span className="font-mono">{coa.batch_number}</span></div>
          </div>
          <button onClick={onCancel} className="text-[#5E636E] hover:text-black" data-testid="edit-coa-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Batch number" v={coa.batch_number} on={(v) => set("batch_number", v)} test="edit-coa-batch" />
          <F label="Product SKU" v={coa.product_sku} on={(v) => set("product_sku", v)} test="edit-coa-sku" />
          <F label="Purity %" v={coa.purity_percent} on={(v) => set("purity_percent", v)} test="edit-coa-purity" />
          <F label="Testing lab" v={coa.testing_lab} on={(v) => set("testing_lab", v)} test="edit-coa-lab" />
          <F label="Test date (YYYY-MM-DD)" v={coa.test_date} on={(v) => set("test_date", v)} test="edit-coa-date" />
          <F label="Retest date (YYYY-MM-DD)" v={coa.retest_date || ""} on={(v) => set("retest_date", v)} test="edit-coa-retest" />
          <F label="Methods (comma-sep)" v={coa.testing_methods} on={(v) => set("testing_methods", v)} test="edit-coa-methods" />
          <div>
            <label className="label-eyebrow block mb-1">Status</label>
            <select
              value={coa.status}
              onChange={(e) => set("status", e.target.value)}
              className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm"
              data-testid="edit-coa-status"
            >
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#E5E7EB] flex items-center justify-between bg-[#F7F7F8]">
          <div className="text-[11px] font-mono text-[#5E636E]">
            {coa.status === "recalled" && (
              <span className="inline-flex items-center gap-1 text-[#A30414]"><AlertTriangle className="h-3 w-3" /> Currently recalled — set status to <b>active</b> to un-recall.</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#5E636E] hover:text-black inline-flex items-center gap-1" data-testid="edit-coa-cancel">
              <RotateCcw className="h-3.5 w-3.5" /> Cancel
            </button>
            <button
              onClick={onSave}
              disabled={busy || !dirty}
              className="btn-primary inline-flex items-center gap-2 disabled:bg-[#5E636E]"
              data-testid="edit-coa-save"
            >
              <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, v, on, test }) => (
  <div>
    <label className="label-eyebrow block mb-1">{label}</label>
    <input value={v || ""} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 border border-[#D1D5DB] bg-white font-mono text-sm" data-testid={test} />
  </div>
);
